import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgencyNav } from '../components/agency/AgencyNav';
import { AgencyFooter } from '../components/agency/AgencyFooter';
import { DiscoveryProvider } from '../components/agency/DiscoveryContext';
import '../components/agency/agency.css';
import { Mail, Clock, Send, Loader2, CheckCircle, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from '../lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Pre-fill subject if coming from a specific service link
    const subjectParam = searchParams.get('subject');
    if (subjectParam === 'team') {
      setFormData(prev => ({
        ...prev,
        subject: 'custom',
        message: 'Hi, I\'m interested in a custom growth setup for my team.\n\nBusiness: \nTeam size: \nTimeline: \n\n'
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/leads/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setSubmitted(true);
        toast.success('Message sent! We\'ll get back to you soon.');
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="agency-page min-h-screen bg-white">
        <DiscoveryProvider>
          <AgencyNav />
          <main className="max-w-2xl mx-auto px-6 pt-32 pb-20">
            <div className="bg-white rounded-2xl p-12 text-center border border-zinc-200 shadow-sm">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-2">Message Sent.</h1>
              <p className="text-zinc-500 mb-6 font-medium">
                Thanks for reaching out. We typically respond within 24 hours · {BRAND.hoursDisplay}.
              </p>
              <a
                href="/"
                className="inline-block px-7 py-3.5 bg-zinc-950 text-white font-semibold rounded-full hover:bg-zinc-800 transition-colors text-sm active:scale-95"
              >
                Back to Home
              </a>
            </div>
          </main>
          <AgencyFooter />
        </DiscoveryProvider>
      </div>
    );
  }

  return (
    <div className="agency-page min-h-screen bg-white">
      <DiscoveryProvider>
        <AgencyNav />

        <main className="max-w-6xl mx-auto px-6 pt-32 pb-16">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600 mb-4">Contact</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter text-zinc-950 mb-4 leading-[1.05]">Get in touch.</h1>
          <p className="text-base md:text-lg text-zinc-500 max-w-2xl mx-auto font-medium leading-relaxed">
            Questions about packages, pricing, timelines or a custom build? Drop us a line — we reply within 24 hours, usually sooner.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 max-w-4xl mx-auto">
          {/* Contact Info Cards */}
          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center" data-testid="contact-info-phone">
            <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Phone className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-[#111111] mb-1 text-sm">Call Us</h3>
            <a href={`tel:${BRAND.phoneTel}`} className="text-blue-600 hover:underline text-sm font-semibold" data-testid="contact-phone-link">
              {BRAND.phoneDisplay}
            </a>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center" data-testid="contact-info-email">
            <div className="w-11 h-11 bg-[#7C3AED]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Mail className="w-5 h-5 text-[#7C3AED]" />
            </div>
            <h3 className="font-semibold text-[#111111] mb-1 text-sm">Email Us</h3>
            <a href={`mailto:${BRAND.email}`} className="text-[#7C3AED] hover:underline text-sm font-semibold" data-testid="contact-email-link">
              {BRAND.email}
            </a>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center" data-testid="contact-info-location">
            <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-[#111111] mb-1 text-sm">Location</h3>
            <p className="text-[#6B7280] text-sm font-medium">{BRAND.city} · {BRAND.serviceArea}</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 text-center" data-testid="contact-info-hours">
            <div className="w-11 h-11 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="font-semibold text-[#111111] mb-1 text-sm">Hours</h3>
            <p className="text-[#6B7280] text-sm font-medium">{BRAND.hoursDisplay}</p>
          </div>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-8 border border-gray-200">
            <h2 className="text-xl font-bold text-[#111111] mb-6">Send us a message</h2>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-zinc-600 mb-2">
                  Subject
                </label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent font-medium"
                >
                  <option value="">Select a topic...</option>
                  <option value="websites">Websites &amp; Digital Experiences</option>
                  <option value="creative-social">Creative &amp; Social</option>
                  <option value="paid-growth">Paid Growth</option>
                  <option value="seo">SEO &amp; Organic Growth</option>
                  <option value="software">Software, Apps &amp; Automation</option>
                  <option value="growth-partnership">Growth Partnership</option>
                  <option value="custom">Something Custom</option>
                  <option value="support">Existing Client Support</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">
                  Message
                </label>
                <textarea
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7C3AED] focus:border-transparent resize-none"
                  placeholder="Tell us how we can help..."
                />
              </div>
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 gradient-btn text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

        <AgencyFooter />
      </DiscoveryProvider>
    </div>
  );
}
